import { useCallback, useEffect, useRef, useState } from 'react';
import { listDevices } from './accounts';
import { controlToken, OPERATOR_EVENT } from './operator';
import { getPairedYouTubeDevice, getYouTubeVenue, safeYouTubeWatchUrl, type YouTubeDevice } from './youtube';
import { DEVICE_PAIRING_EVENT, preferredPairing } from './devicePairing';

export function useYouTubeBroadcast(matchId: string | null) {
  const [token, setToken] = useState(controlToken);
  const [paired, setPaired] = useState(preferredPairing);
  const [device, setDevice] = useState<YouTubeDevice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const identity = useRef({ matchId, token, pairingToken: paired?.token });
  identity.current = { matchId, token, pairingToken: paired?.token };

  useEffect(() => {
    const update = () => { setToken(controlToken()); setPaired(preferredPairing()); };
    window.addEventListener(OPERATOR_EVENT, update);
    window.addEventListener(DEVICE_PAIRING_EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(OPERATOR_EVENT, update);
      window.removeEventListener(DEVICE_PAIRING_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    if ((!token && !paired) || !matchId) return;
    const request = ++generation.current;
    if (paired) {
      const result = await getPairedYouTubeDevice(paired.token, paired.deviceId);
      if (request !== generation.current || identity.current.matchId !== matchId
        || identity.current.pairingToken !== paired.token) return;
      setDevice(result.device.matchId === matchId && result.broadcast.connectionId ? result.broadcast : null);
      setError(null);
      return;
    }
    if (!token) return;
    const owned = (await listDevices(token)).devices;
    const preferredId = localStorage.getItem('aiscoreboard.preferredDeviceId');
    const bound = owned.find(item => item.id === preferredId && item.matchId === matchId)
      ?? owned.find(item => item.matchId === matchId);
    const result = bound ? await getYouTubeVenue(token, bound.venueId) : null;
    if (request !== generation.current || identity.current.matchId !== matchId || identity.current.token !== token) return;
    setDevice(result?.devices.find(item => item.deviceId === bound?.id && item.connectionId) ?? null);
    setError(null);
  }, [token, matchId, paired]);

  useEffect(() => {
    setDevice(null);
    setError(null);
    if ((!token && !paired) || !matchId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async (): Promise<void> => {
      try {
        await refresh();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) timer = setTimeout(() => void poll(), 5000);
      }
    };
    void poll();
    return () => { cancelled = true; clearTimeout(timer); generation.current += 1; };
  }, [token, matchId, paired, refresh]);

  return { device, error, refresh, watchUrl: safeYouTubeWatchUrl(device?.watchUrl ?? null) };
}
